import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Applying Socio-Relational Strength in Well-Being',
    content: [
      '<strong>Learning Goal:</strong> Apply relational strategies to promote mental well-being, recognize the role of mutual support in community health, and commit to ongoing relational self-awareness.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Social support as a protective factor',
      '• Integration with other well-being domains',
      '• Community connectedness and prosocial behavior',
      '• Creating a balanced support ecosystem',
      '<br/>',
      '<strong>Discussion:</strong> Socio-relational well-being doesn\'t exist in isolation–it interacts with and enhances all other aspects of your health. This module shows how to integrate relational strength into your overall well-being strategy.'
    ]
  },
  {
    title: 'Social Support as a Protective Factor',
    content: [
      'Strong social support networks act as protective factors against stress, illness, and mental health challenges.',
      '<br/>',
      '<strong>Stress Buffering:</strong>',
      '• Reduces the harmful effects of stress on the body',
      '• Provides emotional regulation during difficult times',
      '• Offers practical help with problem-solving',
      '• Creates a sense of security and stability',
      '<br/>',
      '<strong>Mental Health Protection:</strong>',
      '• Lowers risk of depression by 40-60%',
      '• Reduces anxiety symptoms',
      '• Improves resilience to trauma',
      '• Enhances coping with life transitions',
      '<br/>',
      '<strong>Physical Health Benefits:</strong>',
      '• Strengthens immune system function',
      '• Improves cardiovascular health',
      '• Speeds recovery from illness',
      '• Increases longevity (2-5 years)',
      '<br/>',
      '<strong>Research Evidence:</strong>',
      '• Harvard Study of Adult Development: Good relationships are the strongest predictor of long life',
      '• Social isolation increases mortality risk similar to smoking 15 cigarettes daily',
      '• Strong social ties reduce risk of Alzheimer\'s disease',
      '• Community support reduces hospital readmission rates'
    ]
  },
  {
    title: 'Integration with Other Well-Being Domains',
    content: [
      'Socio-relational well-being enhances and is enhanced by other aspects of holistic health.',
      '<br/>',
      '<strong>With Emotional Well-Being:</strong>',
      '• Social support helps regulate emotions',
      '• Sharing feelings reduces emotional burden',
      '• Relationships provide validation and perspective',
      '• Group activities improve mood and reduce isolation',
      '<br/>',
      '<strong>With Cognitive Well-Being:</strong>',
      '• Social engagement maintains cognitive function',
      '• Conversations stimulate mental activity',
      '• Group problem-solving enhances critical thinking',
      '• Social learning expands knowledge and skills',
      '<br/>',
      '<strong>With Physical Well-Being:</strong>',
      '• Accountability partners improve exercise adherence',
      '• Group fitness activities provide motivation',
      '• Social dining encourages healthy eating habits',
      '• Walking groups combine physical activity with social connection',
      '<br/>',
      '<strong>With Spiritual Well-Being:</strong>',
      '• Community worship and shared rituals',
      '• Faith-based support groups',
      '• Shared values strengthen bonds',
      '• Collective meaning-making and purpose',
      '<br/>',
      '<strong>Holistic Integration:</strong>',
      '• Strong relationships amplify benefits across all domains',
      '• Weak social support undermines other health efforts',
      '• Balanced well-being requires attention to relationships'
    ]
  },
  {
    title: 'Community Connectedness and Prosocial Behavior',
    content: [
      'Community involvement and helping others create a positive cycle of well-being and social connection.',
      '<br/>',
      '<strong>Community Connectedness:</strong>',
      '• Feeling part of something larger than yourself',
      '• Shared identity and common purpose',
      '• Collective efficacy and mutual support',
      '• Civic engagement and social responsibility',
      '<br/>',
      '<strong>Prosocial Behavior Benefits:</strong>',
      '• Increases happiness and life satisfaction',
      '• Reduces stress and improves mental health',
      '• Creates a sense of meaning and purpose',
      '• Builds social capital and networks',
      '<br/>',
      '<strong>Types of Prosocial Activities:</strong>',
      '• <strong>Volunteering:</strong> Helping others in need',
      '• <strong>Mentoring:</strong> Sharing knowledge and experience',
      '• <strong>Community Service:</strong> Contributing to local improvement',
      '• <strong>Random Acts of Kindness:</strong> Small helpful gestures',
      '• <strong>Environmental Action:</strong> Collective care for the planet',
      '<br/>',
      '<strong>The Helper\'s High:</strong>',
      '• Scientific evidence shows helping others releases endorphins',
      '• Creates positive social feedback loops',
      '• Builds stronger, more interconnected communities',
      '• Enhances both individual and collective well-being'
    ]
  },
  {
    title: 'Creating a Balanced Support Ecosystem',
    content: [
      'A healthy support ecosystem balances giving and receiving across different types of relationships.',
      '<br/>',
      '<strong>Support Balance Principles:</strong>',
      '• <strong>Reciprocity:</strong> Both giving and receiving support',
      '• <strong>Diversity:</strong> Different types of support from different sources',
      '• <strong>Sustainability:</strong> Support that lasts over time',
      '• <strong>Quality over Quantity:</strong> Deep connections matter more than many superficial ones',
      '<br/>',
      '<strong>Building Your Ecosystem:</strong>',
      '• <strong>Inner Circle:</strong> 2-5 close relationships for deep emotional support',
      '• <strong>Middle Circle:</strong> 10-15 moderate relationships for regular interaction',
      '• <strong>Outer Circle:</strong> 50+ loose connections for information and opportunities',
      '<br/>',
      '<strong>Maintenance Strategies:</strong>',
      '• Regular check-ins with important relationships',
      '• Expressing appreciation and gratitude',
      '• Being proactive about staying connected',
      '• Setting boundaries to prevent burnout',
      '<br/>',
      '<strong>Digital Integration:</strong>',
      '• Use technology to maintain long-distance relationships',
      '• Balance online and in-person interactions',
      '• Create digital communities for support',
      '• Mindful social media use for connection, not isolation'
    ]
  },
  {
    title: 'Personal Action Plan Activity',
    content: [
      '<strong>Creating Your Relational Action Plan:</strong> Develop concrete steps to strengthen your socio-relational well-being.',
      '<br/>',
      '<strong>Step 1: Review Your Current State</strong>',
      '• Look back at your MSPSS scores and Support Circle',
      '• Identify your strongest support areas',
      '• Note areas that need improvement',
      '<br/>',
      '<strong>Step 2: Set Specific Goals</strong>',
      '• Write 3 concrete actions for this month',
      '• Make them SMART: Specific, Measurable, Achievable, Relevant, Time-bound',
      '• Focus on one area at a time to avoid overwhelm',
      '<br/>',
      '<strong>Step 3: Examples of Action Steps</strong>',
      '• <strong>Significant Other:</strong> "Plan one deep conversation this week with my closest friend"',
      '• <strong>Family:</strong> "Call my sibling twice this month to catch up"',
      '• <strong>Friends:</strong> "Join a local club or class to meet new people"',
      '• <strong>Community:</strong> "Volunteer 2 hours at a local organization"',
      '• <strong>General:</strong> "Send one thank-you note daily to someone who helped me"',
      '<br/>',
      '<strong>Step 4: Build Accountability</strong>',
      '• Share your plan with a trusted friend',
      '• Track your progress weekly',
      '• Celebrate small wins and adjust as needed',
      '• Be patient–relationship building takes time',
      '<br/>',
      '<strong>Step 5: Long-term Vision</strong>',
      '• Imagine your ideal support network in 6 months',
      '• What relationships do you want to deepen?',
      '• What new connections do you want to cultivate?',
      '• How will stronger relationships improve your life?'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 5: Applying Socio-Relational Strength in Well-Being! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about applying socio-relational well-being principles.',
        'The quiz will help you review key concepts about integrating relationship skills into daily life.'
      ]
    }
  }
];

const SocioRelationalModule5Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('socioRelationalM5');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/socio-relational-module-5/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/socio-relational-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left socio-relational-panel"
        >
          <div className="module-header">
            <h1>Module 5</h1>
            <p className="module-subtitle">Applying Socio-Relational Strength in Well-Being</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Integrate socio-relational skills into daily life, create sustainable social habits, and maintain long-term relationship health.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/socio-relational-module-5/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocioRelationalModule5Page;

